/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WorkerVisibility.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (T159 item 3)
 *     Description     :   The label for Azure rows the collection worker cannot see. Such a row stays in the
 *                         inventory as it was when last seen, with no_access_since set (T151), so a screen
 *                         that lists or counts it says so: WorkerVisibilityNotice above the figures, and
 *                         NoAccessBadge on each row. The owner's rule (2026-10-21): what is due to a
 *                         permission is labelled as such. The notice points at Worker access in the Fabric
 *                         Control portal (FCM's page, path /app/worker-access), which lists each gap and how
 *                         to unlock it; that portal's host differs per environment, so this names the page
 *                         rather than linking to it.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { Alert, AppTooltip, Badge } from '@/components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';

export interface WorkerVisibilityCount {
  /** Plural noun for the kind, as it reads in a sentence: "subscriptions", "storage accounts". */
  noun: string;
  noAccess: number;
  total: number;
}

export interface WorkerVisibilityNoticeProps {
  /** Earliest no_access_since among the counted rows (UTC ISO), or null when the worker sees every row. */
  since: string | null;
  counts: WorkerVisibilityCount[];
  className?: string;
}

const num = new Intl.NumberFormat('en-US');

/** "a", "a and b", "a, b and c". */
function joinList(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export const WorkerVisibilityNotice: React.FC<WorkerVisibilityNoticeProps> = ({ since, counts, className }) => {
  const unseen = counts.filter((c) => c.noAccess > 0);
  if (!since || unseen.length === 0) return null;
  const parts = unseen.map((c) => `${num.format(c.noAccess)} of ${num.format(c.total)} ${c.noun}`);
  return (
    <Alert variant="warning" title="The collection worker can't see part of this estate" className={className ?? 'mb-6'}
      data-testid="worker-visibility-notice">
      <p>
        Since {formatEasternDate(since)}, the collection worker has not been able to see {joinList(parts)}.
        They are shown as they were when last seen and marked No access.
      </p>
      <p className="mt-1">
        To collect them again, open Worker access in the Fabric Control portal: it lists each one and how to give the
        worker access.
      </p>
    </Alert>
  );
};

export interface NoAccessBadgeProps {
  /** The row's no_access_since, UTC ISO. */
  since: string;
}

export const NoAccessBadge: React.FC<NoAccessBadgeProps> = ({ since }) => (
  <AppTooltip title={`The collection worker has not been able to see this since ${formatEasternDate(since)}. What is shown is how it was when last seen.`}>
    <span><Badge variant="warning" size="sm" data-testid="no-access-badge">No access since {formatEasternDate(since)}</Badge></span>
  </AppTooltip>
);
